/**
 * Maps each alphabet (A-Z) to a fixed background color
 * Colors are chosen to provide good contrast with white text
 */
const AVATAR_COLORS = {
  A: '#FF6B6B', // Coral Red
  B: '#4ECDC4', // Turquoise
  C: '#45B7D1', // Sky Blue
  D: '#FFA07A', // Light Salmon
  E: '#98D8C8', // Mint Green
  F: '#F7DC6F', // Yellow
  G: '#BB8FCE', // Lavender
  H: '#85C1E2', // Light Blue
  I: '#F8B739', // Orange
  J: '#52BE80', // Green
  K: '#EC7063', // Pink
  L: '#5DADE2', // Blue
  M: '#F1948A', // Light Red
  N: '#73C6B6', // Teal
  O: '#F4D03F', // Gold
  P: '#AF7AC5', // Purple
  Q: '#48C9B0', // Aqua
  R: '#E74C3C', // Red
  S: '#3498DB', // Bright Blue
  T: '#1ABC9C', // Turquoise Green
  U: '#9B59B6', // Purple
  V: '#E67E22', // Orange
  W: '#16A085', // Dark Turquoise
  X: '#C0392B', // Dark Red
  Y: '#D35400', // Dark Orange
  Z: '#27AE60', // Green
};

/**
 * Gets the first initial from a name
 * Handles edge cases like missing names, empty strings, and lowercase letters
 * @param {string} name - The user's name (first_name, name, or email)
 * @returns {string} - The uppercase first letter, or '?' as fallback
 */
export const getAvatarInitial = (name) => {
  if (!name || typeof name !== 'string') {
    return '?';
  }

  const trimmedName = name.trim();
  if (trimmedName.length === 0) {
    return '?';
  }

  // Get the first character and convert to uppercase
  const firstChar = trimmedName.charAt(0).toUpperCase();
  
  // If it's a letter, return it; otherwise return '?'
  if (/[A-Z]/.test(firstChar)) {
    return firstChar;
  }
  
  return '?';
};

/**
 * Gets the background color for a given initial
 * @param {string} initial - The initial letter (A-Z)
 * @returns {string} - The hex color code, or a default gray color
 */
export const getAvatarColor = (initial) => {
  if (!initial || typeof initial !== 'string') {
    return '#95A5A6'; // Default gray
  }

  const upperInitial = initial.toUpperCase();
  return AVATAR_COLORS[upperInitial] || '#95A5A6'; // Default gray for non-letters
};

/**
 * Gets both the initial and color for a user's name
 * This is the main utility function to use
 * @param {string} name - The user's name (first_name, name, or email)
 * @returns {Object} - Object with initial and color properties
 */
export const getAvatarData = (name) => {
  const initial = getAvatarInitial(name);
  const color = getAvatarColor(initial);
  
  return {
    initial,
    color,
  };
};

